/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, March 30th 2022, 11:02:12 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { PenTool } from "react-feather";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useDefaultProcessTemplateArtifact } from "common/hooks";
import { DigitalSignatureMethodMany2OnePicker } from "service-builder/components/digital-signatures/DigitalSignatureMethodMany2OnePicker";
import { DocumentTemplateMany2OnePicker } from "service-builder/components/documents";
import { DigitalSigningSettings, TaskTemplate } from "lib/schemas";
import { useDigitalSigninSettingsAPI } from "service-builder/services";
import { OSAPIForm, StyledOSDialog, OSActionButton, OSAlert, InputHelpDialogWrapper } from "ui-widgets";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { COLORSCHEME } from "theme/styles";
import { SwitchFormInput } from "ui-widgets";
import { UserTaskCommonSettings } from "./UserTaskCommonSettings";

export const SigningTaskSettings = () => {
  return (
    <>
      <DigitalSignatureMethodMany2OnePicker<TaskTemplate>
        schema_field_key="digital_signature_method"
        label="Choose a Digital Signature Provider"
      />
      <DocumentTemplateMany2OnePicker<TaskTemplate>
        schema_field_key="document_tosign"
        label="Document Template"
        helperText="Document Template of the generated documents to be signed by this task"
      />
      <DigitalSigningSettingsComponent />
      <UserTaskCommonSettings />
    </>
  );
};

const DigitalSigningSettingsComponent = () => {
  const { useList } = useDigitalSigninSettingsAPI();
  const { watch } = useOSFormContext<TaskTemplate>();
  const taskTemplateId = watch("id");
  const { makeRequest } = useList();
  const [currentSettings, setCurrentSettings] = React.useState<DigitalSigningSettings>();

  React.useEffect(() => {
    makeRequest({
      apiQueryParams: { signing_task_template: taskTemplateId },
      setValidAPIResponse: (settingsList) => setCurrentSettings(settingsList[0]),
    });
  }, [taskTemplateId, makeRequest]);

  return taskTemplateId ? (
    <StyledOSDialog
      popUpTitle="Advanced Digital Signing Settings"
      popUpIcon={<PenTool color={COLORSCHEME.primary} />}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton fullWidth variant="contained" onClick={handleOpen}>
          Advanced Digital Signing Settings
        </OSActionButton>
      )}
      renderContentWithClose={({ handleClose }) => (
        <DigitalSigningSettingsForm
          currentSettings={currentSettings}
          setCurrentSettings={setCurrentSettings}
          taskTemplateId={taskTemplateId}
        />
      )}
    />
  ) : null;
};

const DigitalSigningSettingsForm = ({ currentSettings, setCurrentSettings, taskTemplateId }) => {
  const [useJSON, setUseJSON] = React.useState(false);
  const { useAPIForm } = useDigitalSigninSettingsAPI();
  const defaultProcessTemplateArtifact: DigitalSigningSettings = {
    ...useDefaultProcessTemplateArtifact(),
    signing_task_template: taskTemplateId,
    settings: { set_signers_automatically: false },
  };

  const useAPIFormReturn = useAPIForm({
    id: currentSettings?.id,
    onAPISave: (settings) => {
      setCurrentSettings(settings);
    },
    defaultValues: currentSettings || defaultProcessTemplateArtifact,
  });

  const [isInvalidJSON, setIsInvalidJSON] = React.useState(false);

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} submitButtonProps={{ disabled: isInvalidJSON }}>
      <InputHelpDialogWrapper
        helpTitle="Manage JSON settings"
        helpContent="Set complex digital signing settings using JSON. This can be useful if you have custom digital signing provider that needs custom settings."
      >
        <FormControlLabel
          control={<Switch value={useJSON} onChange={(event) => setUseJSON(event.target.checked)} />}
          label="Manage your digital signing settings using JSON"
        />
      </InputHelpDialogWrapper>
      {useJSON ? (
        <SettingsAsJSON isInvalidJSON={isInvalidJSON} setIsInvalidJSON={setIsInvalidJSON} />
      ) : (
        <InputHelpDialogWrapper
          helpTitle="Set Signers automatically"
          helpContent="If this is set to true, all users that have team access to this task will be automatically set as signers for the document."
        >
          <SwitchFormInput schema_field_key="settings.set_signers_automatically" label="Set Signers automatically" />
        </InputHelpDialogWrapper>
      )}
    </OSAPIForm>
  );
};

const SettingsAsJSON = ({ isInvalidJSON, setIsInvalidJSON }) => {
  const { watch, setValue } = useOSFormContext<DigitalSigningSettings>();
  const settings = watch("settings");
  const onChange = (settings: string) => {
    try {
      setValue("settings", JSON.parse(settings));
      setIsInvalidJSON(false);
    } catch (error) {
      console.error("Invalid JSON", error);
      setIsInvalidJSON(true);
    }
  };

  return (
    <>
      {isInvalidJSON && (
        <OSAlert severity="error" variant="filled" alertTitle={<>Invalid JSON</>}>
          Please enter a valid JSON object.
        </OSAlert>
      )}
      <TextField
        defaultValue={JSON.stringify(settings)}
        onBlur={(event) => onChange(event.target.value)}
        multiline
        rows={4}
      />
    </>
  );
};
